import React, { useEffect, useState } from 'react';
import { Loader2, Send } from 'lucide-react';
import { vendorsAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED'];
const BASIS_OPTIONS = [
  { value: 'per_piece', label: 'per piece' },
  { value: 'per_100', label: 'per 100 pcs' },
  { value: 'per_1000', label: 'per 1000 pcs' },
];

export const VendorRfqDialog = ({ open, onOpenChange, selectedCount, onCreate, saving }) => {
  const [vendors, setVendors] = useState([]);
  const [vendorId, setVendorId] = useState('');
  const [currency, setCurrency] = useState('INR');
  const [priceBasis, setPriceBasis] = useState('per_piece');
  const [dueDate, setDueDate] = useState('');
  const [remarks, setRemarks] = useState('');

  useEffect(() => {
    if (!open) return;
    vendorsAPI.list({ is_active: true }).then((response) => setVendors(response.data.items || []))
      .catch(() => toast.error('Failed to load vendors'));
  }, [open]);

  const pickVendor = (value) => {
    setVendorId(value);
    const vendor = vendors.find((item) => item.id === value);
    const isIndian = (vendor?.country || 'India').trim().toLowerCase() === 'india';
    setCurrency(isIndian ? 'INR' : 'USD');
    setPriceBasis(isIndian ? 'per_piece' : 'per_1000');
  };

  const selectedVendor = vendors.find((item) => item.id === vendorId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="vendor-rfq-dialog">
        <DialogHeader><DialogTitle>Generate vendor RFQ</DialogTitle>
          <DialogDescription>{selectedCount} selected inquiry item(s) will be copied into a branded vendor request.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-3">
          <div className="space-y-2"><Label>Vendor *</Label>
            <Select value={vendorId} onValueChange={pickVendor}>
              <SelectTrigger data-testid="vendor-rfq-vendor-select"><SelectValue placeholder="Select vendor" /></SelectTrigger>
              <SelectContent>{vendors.map((vendor) => <SelectItem key={vendor.id} value={vendor.id}>{vendor.vendor_code} · {vendor.name}{vendor.country ? ` · ${vendor.country}` : ''}</SelectItem>)}</SelectContent>
            </Select>
            {selectedVendor && (
              <p className="text-xs text-muted-foreground" data-testid="vendor-rfq-currency-hint">
                {(selectedVendor.country || 'India').trim().toLowerCase() === 'india'
                  ? 'Indian vendor — defaulted to INR per piece.'
                  : `${selectedVendor.country} vendor — defaulted to USD per 1000 pcs.`}
              </p>
            )}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2"><Label>Quote currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger data-testid="vendor-rfq-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((code) => <SelectItem key={code} value={code}>{code}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Price basis</Label>
              <Select value={priceBasis} onValueChange={setPriceBasis}>
                <SelectTrigger data-testid="vendor-rfq-basis-select"><SelectValue /></SelectTrigger>
                <SelectContent>{BASIS_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2"><Label>Response due date</Label>
            <Input type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} data-testid="vendor-rfq-due-date-input" />
          </div>
          <div className="space-y-2"><Label>Remarks</Label>
            <Textarea value={remarks} onChange={(event) => setRemarks(event.target.value)} data-testid="vendor-rfq-remarks-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="vendor-rfq-cancel-button">Cancel</Button>
          <Button onClick={() => onCreate({
            vendor_id: vendorId, currency, price_basis: priceBasis,
            due_date: dueDate || null, remarks: remarks || null,
          })}
            disabled={!vendorId || saving} data-testid="vendor-rfq-create-button">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Create RFQ
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
